import { supabase } from '../supabase';
import { todayLocalDateStr, localDateStr } from './lifeAdminData';

/* ============================================
   CONSTANTS
   ============================================ */

export const PRIORITY_CONFIG = {
  high:   { label: 'High',   color: '#8b2c2c', order: 0 },
  medium: { label: 'Medium', color: '#b8870c', order: 1 },
  low:    { label: 'Low',    color: '#7a9b76', order: 2 },
};

export const PRIORITY_ORDER = ['high', 'medium', 'low'];

export const DEFAULT_CATEGORIES = [
  { name: 'work',     label: 'Work',     color: '#0d4f5c' },
  { name: 'personal', label: 'Personal', color: '#9d6eff' },
  { name: 'health',   label: 'Health',   color: '#7a9b76' },
  { name: 'errand',   label: 'Errand',   color: '#b8870c' },
];

export const CATEGORY_BY_NAME = DEFAULT_CATEGORIES.reduce((acc, c) => {
  acc[c.name] = c;
  return acc;
}, {});

/* ============================================
   PROMPT — planner insight
   ============================================ */

export const PLANNER_INSIGHT_PROMPT = ({ tasksByDay, todayTasks, currentDate }) => `You are the Planner module of Curx AI, a thoughtful coach helping the user understand how they actually spend their days.

Read the user's last 7 days of tasks (completed + incomplete). Identify 2-3 specific patterns and 2-3 actionable suggestions, then close with one warm encouragement that feels honest, not saccharine.

Current date: ${currentDate}
Today's tasks: ${todayTasks.length} (${todayTasks.filter(t => t.is_completed).length} done, ${todayTasks.filter(t => !t.is_completed).length} pending)

LAST 7 DAYS OF TASKS:
${Object.entries(tasksByDay)
  .sort((a, b) => b[0].localeCompare(a[0]))
  .map(([date, tasks]) => {
    const done = tasks.filter(t => t.is_completed).length;
    return `${date} (${done}/${tasks.length} done):\n${tasks.map(t => `  - [${t.is_completed ? 'x' : ' '}] ${t.priority.toUpperCase()} · ${t.category} · ${t.title}`).join('\n')}`;
  })
  .join('\n\n') || '(no tasks logged in the past 7 days)'}

Output ONLY valid JSON, no markdown:

{
  "observations": ["<1-sentence pattern 1, with specific reference to days/categories>", "<pattern 2>", "<pattern 3>"],
  "suggestions": ["<actionable suggestion 1>", "<suggestion 2>", "<suggestion 3>"],
  "encouragement": "<one warm sentence — honest, not saccharine>"
}

Be specific. Reference categories, days of the week, priority levels. No platitudes.`;

/* ============================================
   SUPABASE — READ
   ============================================ */

export async function loadTasksForDate(userId, date) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('planner_tasks')
    .select('*')
    .eq('user_id', userId)
    .eq('date', date)
    .order('is_completed', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function loadTasksForRange(userId, startDate, endDate) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('planner_tasks')
    .select('*')
    .eq('user_id', userId)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: false })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function loadIncompleteTasksForDate(userId, date) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('planner_tasks')
    .select('*')
    .eq('user_id', userId)
    .eq('date', date)
    .eq('is_completed', false)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

/* ============================================
   SUPABASE — WRITE
   ============================================ */

export async function createTask({
  user_id, date, title,
  description, priority = 'medium', category = 'personal',
  estimated_duration_min,
}) {
  if (!user_id) throw new Error('Missing user_id');
  if (!String(title || '').trim()) throw new Error('Title is required');
  const payload = {
    user_id,
    date: date || todayLocalDateStr(),
    title: String(title).trim().slice(0, 200),
    description: description ? String(description).trim() : null,
    priority,
    category,
    estimated_duration_min: estimated_duration_min ? Math.max(1, Math.round(Number(estimated_duration_min))) : null,
  };
  const { data, error } = await supabase
    .from('planner_tasks')
    .insert(payload)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Save didn't write any rows. Check your RLS policy for INSERT on planner_tasks.");
  }
  return data[0];
}

export async function updateTask(taskId, fields) {
  if (!taskId) throw new Error('Missing task id');
  const payload = { ...fields, updated_at: new Date().toISOString() };
  // Sanitize known fields
  if ('title' in payload && typeof payload.title === 'string') {
    payload.title = payload.title.trim().slice(0, 200);
  }
  if ('description' in payload && payload.description != null) {
    payload.description = String(payload.description).trim() || null;
  }
  if ('estimated_duration_min' in payload) {
    payload.estimated_duration_min = payload.estimated_duration_min
      ? Math.max(1, Math.round(Number(payload.estimated_duration_min)))
      : null;
  }
  const { data, error } = await supabase
    .from('planner_tasks')
    .update(payload)
    .eq('id', taskId)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Update didn't write any rows. Check your RLS policy for UPDATE on planner_tasks.");
  }
  return data[0];
}

export async function toggleTaskComplete(taskId, isCompleted) {
  return updateTask(taskId, {
    is_completed: !!isCompleted,
    completed_at: isCompleted ? new Date().toISOString() : null,
  });
}

export async function deleteTask(taskId) {
  if (!taskId) throw new Error('Missing task id');
  const { data, error } = await supabase
    .from('planner_tasks')
    .delete()
    .eq('id', taskId)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Delete didn't affect any rows. Check your RLS policy for DELETE on planner_tasks.");
  }
}

export async function rolloverTask(taskId, toDate) {
  return updateTask(taskId, { date: toDate });
}

export async function rolloverAllIncomplete(userId, fromDate, toDate) {
  if (!userId) throw new Error('Missing user_id');
  const incomplete = await loadIncompleteTasksForDate(userId, fromDate);
  const results = [];
  for (const task of incomplete) {
    try {
      const updated = await rolloverTask(task.id, toDate);
      results.push(updated);
    } catch (_) { /* per-task failures don't abort the bulk op */ }
  }
  return results;
}

/* ============================================
   AGGREGATIONS
   ============================================ */

export function getTodaysProgress(tasks) {
  const total = (tasks || []).length;
  const completed = (tasks || []).filter(t => t.is_completed).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { total, completed, percent };
}

export function groupByPriority(tasks) {
  const groups = { high: [], medium: [], low: [] };
  for (const t of (tasks || [])) {
    const p = PRIORITY_CONFIG[t.priority] ? t.priority : 'medium';
    groups[p].push(t);
  }
  return groups;
}

// Heuristic: count today's tasks where created_at::date < today.
// Captures both manual rollover (UPDATE date) and bulk roll-over-all.
// Per spec Option B — no schema change required.
export function countRolledOverTasks(todayTasks, todayDateStr) {
  return (todayTasks || []).filter(t => {
    if (!t.created_at) return false;
    const created = localDateStr(new Date(t.created_at));
    return created < todayDateStr;
  }).length;
}

// Cross-tab: compact summary for Today's AI prompt. Returns null if zero tasks.
export async function getPlannerSummaryForToday(userId) {
  try {
    if (!userId) return null;
    const today = todayLocalDateStr();
    const todayTasks = await loadTasksForDate(userId, today);
    if (!todayTasks || todayTasks.length === 0) return null;
    const high_priority = todayTasks.filter(t => t.priority === 'high').length;
    const rollover_count = countRolledOverTasks(todayTasks, today);
    return {
      total_today: todayTasks.length,
      high_priority,
      rollover_count,
    };
  } catch (_) {
    return null;
  }
}

/* ============================================
   AI INSIGHT
   ============================================ */

export async function generatePlannerInsight({ tasks, todayDateStr }) {
  // Group by date
  const tasksByDay = {};
  for (const t of (tasks || [])) {
    if (!tasksByDay[t.date]) tasksByDay[t.date] = [];
    tasksByDay[t.date].push(t);
  }
  const todayTasks = tasksByDay[todayDateStr] || [];
  const prompt = PLANNER_INSIGHT_PROMPT({
    tasksByDay,
    todayTasks,
    currentDate: todayDateStr,
  });
  const response = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model: 'sonnet' }),
  });
  const data = await response.json();
  const text = (data?.content || []).map(i => i.text || '').join('').trim();
  const parsed = tryParseJSONObject(text);
  if (!parsed || !Array.isArray(parsed.observations) || !Array.isArray(parsed.suggestions)) {
    throw new Error('Planner insight returned invalid shape');
  }
  return {
    observations:  parsed.observations.map(s => String(s).trim()).filter(Boolean).slice(0, 3),
    suggestions:   parsed.suggestions.map(s => String(s).trim()).filter(Boolean).slice(0, 3),
    encouragement: String(parsed.encouragement || '').trim(),
  };
}

/* ============================================
   HELPERS
   ============================================ */

function tryParseJSONObject(text) {
  if (!text) return null;
  try { return JSON.parse(text); } catch (_) { /* fallthrough */ }
  const match = String(text).match(/\{[\s\S]*\}/);
  if (match) {
    try { return JSON.parse(match[0]); } catch (__) { /* fallthrough */ }
  }
  return null;
}

export function tomorrowLocalDateStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return localDateStr(d);
}

export function yesterdayLocalDateStr() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return localDateStr(d);
}

export function pastNDaysRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return { start: localDateStr(start), end: localDateStr(end) };
}
